import React, { useState } from 'react';

import { Card, Skeleton, Typography } from '@mui/material';
import clsx from 'clsx';
import type { WorkflowCollaborator, WorkflowCollaboratorCardVariant } from 'src/components';
import {
  Spacer,
  TextTabSwitch,
  useCollabCardStyles,
  useTabStyles,
  WhatsThis,
  WorkflowCollaboratorCard,
  WorkflowTeamToolbar,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { DateKey, Direction, getDateComparator } from 'src/helpers';
import { useGetWorkflowPermissions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  tabContainer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  tabText: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  teamContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface WorkflowTeamProps {
  variant: Apollo.WorkflowVariant;
  workflowId: UUID;
}

export function ContractWorkflowCollaborators({
  className,
  variant,
  workflowId,
}: {
  workflowId: UUID;
  className?: string;
  variant?: WorkflowCollaboratorCardVariant;
}): JSX.Element {
  const { classes } = useStyles();
  const { loading, permissions } = useGetWorkflowPermissions(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  return (
    <div className={clsx(classes.teamContainer, className)}>
      <WorkflowTeamToolbar workflowId={workflowId} />
      <CollaboratorList
        collaborators={permissions}
        loading={loading}
        variant={variant}
        workflowId={workflowId}
      />
    </div>
  );
}

export enum TemplateCollaboratorsTab {
  TemplateCollaborators,
  WorkflowCollaborators,
}

export function TemplateWorkflowCollaborators({
  workflowId,
}: Pick<WorkflowTeamProps, 'workflowId'>): JSX.Element {
  const { classes } = useStyles();
  const { classes: tabClasses } = useTabStyles();
  const { loading, permissions } = useGetWorkflowPermissions(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [tab, setTab] = useState<TemplateCollaboratorsTab>(
    TemplateCollaboratorsTab.TemplateCollaborators
  );
  const { launchCollaborators, templateCollaborators } = permissions.reduce<{
    launchCollaborators: WorkflowCollaborator[];
    templateCollaborators: WorkflowCollaborator[];
  }>(
    (acc, collaborator) => {
      switch (collaborator.timeliness) {
        case Apollo.WorkflowPermissionTimeliness.Active:
          acc.templateCollaborators.push(collaborator);
          return acc;
        case Apollo.WorkflowPermissionTimeliness.Queued:
          acc.launchCollaborators.push(collaborator);
          return acc;
      }
    },
    { launchCollaborators: [], templateCollaborators: [] }
  );
  const relevantCollaborators =
    tab === TemplateCollaboratorsTab.TemplateCollaborators
      ? templateCollaborators
      : launchCollaborators;

  return (
    <div className={clsx(tabClasses.tabContent, classes.teamContainer)}>
      <div className={classes.tabContainer}>
        <TextTabSwitch
          leftText="Template collaborators"
          rightText="Workflow collaborators"
          selectedSide={tab === TemplateCollaboratorsTab.TemplateCollaborators ? 'left' : 'right'}
          onLeftClick={() => setTab(TemplateCollaboratorsTab.TemplateCollaborators)}
          onRightClick={() => setTab(TemplateCollaboratorsTab.WorkflowCollaborators)}
        />
        <WhatsThis
          hintText="Template vs. workflow collaborators?"
          tooltipMsg='"Template collaborators" have access to the template and can launch or edit, depending on access. "Workflow collaborators" receive access to any workflow launched from this template.'
          tooltipPlacement="right"
        />
      </div>
      <WorkflowTeamToolbar templateTab={tab} workflowId={workflowId} />
      <CollaboratorList
        collaborators={relevantCollaborators}
        loading={loading}
        templateTab={tab}
        workflowId={workflowId}
      />
    </div>
  );
}

const useCollaboratorListStyles = makeStyles()(theme => ({
  buttonLoading: {
    borderRadius: 16,
  },
  cardList: {
    alignItems: 'stretch',
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fill, minmax(208px, 1fr))',
    gridTemplateRows: 'min-content',
    width: '100%',
  },
  empty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    height: '100%',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    width: '100%',
  },
}));

interface CollaboratorListProps {
  collaborators: WorkflowCollaborator[];
  loading: boolean;
  workflowId: UUID;
  templateTab?: TemplateCollaboratorsTab;
  variant?: WorkflowCollaboratorCardVariant;
}

export function CollaboratorList({
  collaborators,
  loading,
  templateTab,
  variant,
  workflowId,
}: CollaboratorListProps): JSX.Element {
  const { classes: cardClasses } = useCollabCardStyles();
  const { classes } = useCollaboratorListStyles();
  const sortedCollaborators = sortCollaborators(collaborators);

  if (loading)
    return (
      <div className={classes.cardList}>
        {Array.from({ length: 2 }).map((_, index) => {
          return (
            <Card key={index} className={cardClasses.card}>
              <Skeleton height={40} variant="circular" width={40} />
              <Skeleton variant="text" width={64} />
              <Spacer />
              <Skeleton
                className={classes.buttonLoading}
                height={32}
                variant="rectangular"
                width="100%"
              />
            </Card>
          );
        })}
      </div>
    );

  if (!sortedCollaborators.length)
    return (
      <div className={classes.empty}>
        <Typography align="center" variant="subtitle1">
          No Collaborators
        </Typography>
        <Typography align="center" color="textSecondary">
          Add a collaborator above
        </Typography>
      </div>
    );

  return (
    <div className={classes.cardList}>
      {sortedCollaborators.map(collaborator => (
        <WorkflowCollaboratorCard
          key={collaborator.id}
          collaborator={collaborator}
          templateTab={templateTab}
          variant={variant}
          workflowId={workflowId}
        />
      ))}
    </div>
  );
}

export function sortCollaborators<
  T extends Pick<Apollo.WorkflowPermission, 'createdAt' | '__typename'>,
>(collaborators: T[]): T[] {
  return [...collaborators].sort(
    getDateComparator({ dateKey: DateKey.createdAt, direction: Direction.Ascending })
  );
}
